import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Helmet } from "react-helmet";
import { Link } from "wouter";
import ManagerLayout from "@/components/layout/ManagerLayout";
import { apiRequest } from "@/lib/api";

// UI Components
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";

// Icons
import {
  Search,
  Plus,
  MessageSquare,
  ThumbsUp,
  Filter,
  Tag,
  Eye,
  Clock,
  CornerUpRight,
  BookMarked,
  CheckCircle,
  User,
} from "lucide-react";
import { format } from "date-fns";

type CommunityQuestion = {
  id: number;
  title: string;
  content: string;
  authorId: number;
  authorName?: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  upvotes: number;
  downvotes: number;
  views: number;
  isModeratorPinned: boolean;
  tags: Array<{ id: number; name: string }>;
  answerCount?: number;
};

type CommunityTag = {
  id: number;
  name: string;
  description: string | null;
  createdAt: Date;
};

export default function CommunityPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTag, setSelectedTag] = useState<number | null>(null);
  const [sortBy, setSortBy] = useState("latest");
  const [questions, setQuestions] = useState<CommunityQuestion[]>([]);

  // Fetch questions
  const {
    data: questionsData,
    isLoading: isLoadingQuestions,
    error: questionsError,
    refetch: refetchQuestions
  } = useQuery({
    queryKey: ['community/questions'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'community/questions');
        
        
        
        const data =  response;
        setQuestions(data);
        return data;
      } catch (error) {
        console.error("Error fetching community questions:", error);
        throw error;
      }
    }
  });

  // Fetch tags
  const {
    data: tags,
    isLoading: isLoadingTags,
    error: tagsError,
    refetch: refetchTags
  } = useQuery({
    queryKey: ['community/tags'],
    enabled: !!user,
    queryFn: async () => {
      try {
        const response = await apiRequest('GET', 'community/tags');
        
        
        return response;
      } catch (error) {
        console.error("Error fetching community tags:", error);
        throw error;
      }
    }
  });

  // Filter and sort questions
  const filteredQuestions = questionsData
    ? questionsData
        .filter((question: CommunityQuestion) => {
          // Apply search filter
          if (
            searchQuery &&
            !question.title
              .toLowerCase()
              .includes(searchQuery.toLowerCase()) &&
            !question.content
              .toLowerCase()
              .includes(searchQuery.toLowerCase())
          ) {
            return false;
          }

          // Apply tag filter
          if (
            selectedTag &&
            !question.tags.some((tag) => tag.id === selectedTag)
          ) {
            return false;
          }

          return true;
        })
        .sort((a: CommunityQuestion, b: CommunityQuestion) => {
          // Apply sorting
          switch (sortBy) {
            case "latest":
              return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
            case "popular":
              return b.views - a.views;
            case "mostVoted":
              return b.upvotes - a.upvotes;
            default:
              return 0;
          }
        })
    : [];

  if (questionsError || tagsError) {
    console.error("Community error details:", { questionsError, tagsError });
    return (
      <div className="container mx-auto py-6">
        <h1 className="text-2xl font-bold mb-4">Manager Community</h1>
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <p className="text-red-700 font-medium">
            Error loading community data. Please try again later.
          </p>
          <p className="text-sm text-red-600 mt-2">
            {questionsError instanceof Error ? questionsError.message : ''}
            {tagsError instanceof Error ? tagsError.message : ''}
          </p>
          <Button 
            variant="outline" 
            className="mt-3"
            onClick={() => window.location.reload()}
          >
            Retry Loading
          </Button>
        </div>
      </div>
    );
  }

  return (
    <ManagerLayout>
      <Helmet>
        <title>Manager Community | StayMiddle</title>
      </Helmet>

      <div className="container mx-auto py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold">Manager Community</h1>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Ask a Question
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="md:col-span-3">
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search questions..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select
                  value={sortBy}
                  onValueChange={(value) => setSortBy(value)}
                >
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="latest">Latest</SelectItem>
                    <SelectItem value="popular">Most Viewed</SelectItem>
                    <SelectItem value="mostVoted">Most Upvoted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <p className="text-sm text-muted-foreground">
                {filteredQuestions?.length || 0} questions
              </p>
            </div>

            {isLoadingQuestions ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <Card key={i} className="bg-muted/40">
                    <CardHeader>
                      <div className="h-6 w-2/3 bg-muted rounded animate-pulse"></div>
                    </CardHeader>
                    <CardContent>
                      <div className="h-4 w-full bg-muted rounded animate-pulse mb-2"></div>
                      <div className="h-4 w-5/6 bg-muted rounded animate-pulse"></div>
                    </CardContent>
                    <CardFooter>
                      <div className="h-4 w-1/3 bg-muted rounded animate-pulse"></div>
                    </CardFooter>
                  </Card>
                ))}
              </div>
            ) : filteredQuestions?.length > 0 ? (
              <div className="space-y-4">
                {filteredQuestions.map((question: CommunityQuestion) => (
                  <Link
                    key={question.id}
                    href={`/manager/community/${question.id}`}
                  >
                    <Card className="cursor-pointer hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-xl">{question.title}</CardTitle>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {question.tags.map((tag) => (
                            <Badge key={tag.id} variant="outline">
                              {tag.name}
                            </Badge>
                          ))}
                        </div>
                      </CardHeader>
                      <CardContent className="pb-2">
                        <p className="line-clamp-2 text-muted-foreground">
                          {question.content}
                        </p>
                      </CardContent>
                      <CardFooter className="pt-2 flex justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-4">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="h-3.5 w-3.5" />
                            {question.answerCount || 0} answers
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="h-3.5 w-3.5" />
                            {question.upvotes} upvotes
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3.5 w-3.5" />
                            {question.views} views
                          </span>
                        </div>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {format(new Date(question.createdAt), "MMM d, yyyy")}
                        </span>
                      </CardFooter>
                    </Card>
                  </Link>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="pt-6 pb-6 text-center">
                  <p className="text-muted-foreground mb-2">
                    No questions found matching your criteria.
                  </p>
                  {searchQuery || selectedTag ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSearchQuery("");
                        setSelectedTag(null);
                      }}
                    >
                      Clear Filters
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      onClick={() => setIsCreateDialogOpen(true)}
                    >
                      Ask the First Question
                    </Button>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Popular Tags</CardTitle>
                <CardDescription>
                  Filter questions by these topics
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoadingTags ? (
                  <div className="space-y-2">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className="h-6 bg-muted rounded animate-pulse"
                      ></div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant={selectedTag === null ? "default" : "outline"}
                      size="sm"
                      className="mb-2"
                      onClick={() => setSelectedTag(null)}
                    >
                      All Tags
                    </Button>
                    {tags?.map((tag: CommunityTag) => (
                      <Button
                        key={tag.id}
                        variant={selectedTag === tag.id ? "default" : "outline"}
                        size="sm"
                        className="mb-2"
                        onClick={() => setSelectedTag(tag.id)}
                      >
                        {tag.name}
                      </Button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Community Stats</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Total Questions</span>
                    <span className="font-medium">{questionsData?.length || 0}</span>
                  </div>
                  <Progress value={questionsData?.length || 0} max={100} />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Total Answers</span>
                    <span className="font-medium">
                      {questionsData?.reduce(
                        (sum: number, q: CommunityQuestion) =>
                          sum + (q.answerCount || 0),
                        0
                      ) || 0}
                    </span>
                  </div>
                  <Progress
                    value={
                      questionsData?.reduce(
                        (sum: number, q: CommunityQuestion) =>
                          sum + (q.answerCount || 0),
                        0
                      ) || 0
                    }
                    max={100}
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-sm">Active Tags</span>
                    <span className="font-medium">{tags?.length || 0}</span>
                  </div>
                  <Progress value={tags?.length || 0} max={20} />
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>About the Community</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground">
                  This is a dedicated space for property managers to discuss best
                  practices, share experiences, and help each other succeed in
                  the Dubai rental market.
                </p>
                <div className="flex flex-col gap-2 mt-4">
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Ask questions about property management</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Share your expertise with other managers</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Learn from the collective wisdom</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Ask a New Question</DialogTitle>
            <DialogDescription>
              Share your question with the property manager community.
            </DialogDescription>
          </DialogHeader>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const title = formData.get("title") as string;
              const content = formData.get("content") as string;
              const existingTags = formData.getAll("tags")
                .map(tag => parseInt(tag as string))
                .filter(tag => !isNaN(tag) && tag !== null);
              const newTagsInput = formData.get("newTags") as string;
              
              // Parse new tags
              const newTags = newTagsInput
                ? newTagsInput.split(",")
                    .map(tag => tag.trim())
                    .filter(tag => tag.length > 0)
                    .map(tag => ({ name: tag }))
                : [];

              if (!title || !content) {
                toast({
                  title: "Missing information",
                  description: "Please fill in all required fields.",
                  variant: "destructive",
                });
                return;
              }

              // Call API to create question
              apiRequest('POST', 'community/questions', {
                title,
                content,
                tags: [...existingTags, ...newTags]
              })
                .then(res => {
                  // if (!res.ok) {
                  //   throw new Error(`Failed to create question: ${res.statusText}`);
                  // }
                  // return res.json();
                  return res;
                })
                .then((data) => {
                  toast({
                    title: "Question submitted",
                    description: "Your question has been posted to the community.",
                  });
                  setIsCreateDialogOpen(false);
                  // Refresh both questions and tags
                  refetchQuestions();
                  refetchTags();
                })
                .catch(error => {
                  console.error("Error creating question:", error);
                  toast({
                    title: "Error",
                    description: error.message || "Failed to create question. Please try again.",
                    variant: "destructive",
                  });
                });
            }}
            className="space-y-4"
          >
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                name="title"
                placeholder="What's your question about?"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Details</Label>
              <Textarea
                id="content"
                name="content"
                placeholder="Provide details about your question..."
                rows={5}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="tags">Tags</Label>
              <Select
                name="tags"
                onValueChange={(value) => {
                  const form = document.querySelector('form');
                  if (form) {
                    const currentTags = new FormData(form).getAll("tags");
                    if (!currentTags.includes(value)) {
                      const input = document.createElement("input");
                      input.type = "hidden";
                      input.name = "tags";
                      input.value = value;
                      form.appendChild(input);
                    }
                  }
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select tags" />
                </SelectTrigger>
                <SelectContent>
                  {tags?.map((tag: CommunityTag) => (
                    <SelectItem key={tag.id} value={tag.id.toString()}>
                      {tag.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex flex-wrap gap-2 mt-2">
                {Array.from(document.querySelectorAll('input[name="tags"]')).map((input: Element) => {
                  const tagId = (input as HTMLInputElement).value;
                  const tag = tags?.find((t: CommunityTag) => t.id.toString() === tagId);
                  return tag ? (
                    <Badge key={tag.id} variant="secondary" className="flex items-center gap-1">
                      {tag.name}
                      <button
                        type="button"
                        onClick={() => {
                          const form = document.querySelector('form');
                          if (form) {
                            const inputs = form.querySelectorAll(`input[name="tags"][value="${tagId}"]`);
                            inputs.forEach((input: Element) => input.remove());
                          }
                        }}
                        className="ml-1 hover:text-destructive"
                      >
                        ×
                      </button>
                    </Badge>
                  ) : null;
                })}
              </div>
              <div className="mt-2">
                <Label htmlFor="newTags">New Tags (comma-separated)</Label>
                <Input
                  id="newTags"
                  name="newTags"
                  placeholder="Enter new tags, separated by commas"
                  className="mt-1"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Select existing tags or create new ones to categorize your question
              </p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCreateDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">Post Question</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </ManagerLayout>
  );
}